import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useGetUserStatisticsQuery,
  useGetAdminCategoriesQuery,
  useGetProductsQuery,
  useGetMaintenanceStatusQuery,
  useUpdateMaintenanceModeMutation,
  useGetDisabledServersQuery,
  useToggleServerDisabledMutation,
  useGetProxy5gPackagesVisibilityQuery,
  useUpdateProxy5gPackageVisibilityMutation,
  useGetMasterBalanceQuery,
} from '../../store/api/adminApi';
import { ButtonSpinner } from '../../components/ui/Loader';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { IS_TENANT_MODE } from '../../config/tenantMode';
import { useGetServerLabelsQuery } from '../../store/api/adminApi';
import {
  DASHBOARD_SERVER_GROUPS,
  flattenServerLabelGroups,
  resolveServerLabel,
} from '../../utils/serverDisplayNames';
import { useTenantPlanFeatures } from '../../hooks/useTenantPlanFeatures';
import { TenantMerchantDashboard } from './TenantMerchantDashboard';

export function AdminDashboardPage() {
  const { t } = useTranslation();
  const isTenantMode = IS_TENANT_MODE;
  const { hasNumbers, hasProxies } = useTenantPlanFeatures();

  const { data: masterBalanceData, isLoading: masterBalanceLoading } = useGetMasterBalanceQuery(undefined, {
    skip: !isTenantMode,
  });

  const { data: userStatsData, isLoading: userStatsLoading } = useGetUserStatisticsQuery();
  const { data: categoriesData, isLoading: categoriesLoading } = useGetAdminCategoriesQuery(
    { includeHidden: true },
    { skip: isTenantMode }
  );
  const { data: allProductsData, isLoading: allProductsLoading } = useGetProductsQuery(
    { limit: 100 },
    { skip: isTenantMode }
  );
  const { data: availableProductsData } = useGetProductsQuery(
    { status: 'available', limit: 1 },
    { skip: isTenantMode }
  );
  const { data: soldProductsData } = useGetProductsQuery(
    { status: 'sold', limit: 1 },
    { skip: isTenantMode }
  );

  const { data: maintenanceData, isLoading: maintenanceLoading } = useGetMaintenanceStatusQuery();
  const [updateMaintenanceMode, { isLoading: isUpdatingMaintenance }] = useUpdateMaintenanceModeMutation();

  const currentMode = maintenanceData?.data?.maintenance?.mode || 'none';
  const [confirmMode, setConfirmMode] = useState<string | null>(null);

  // Server-specific maintenance
  const { data: disabledServersData } = useGetDisabledServersQuery();
  const [toggleServerDisabled, { isLoading: isTogglingServer }] = useToggleServerDisabledMutation();
  const [showServerModal, setShowServerModal] = useState(false);
  const [serverConfirm, setServerConfirm] = useState<{ serverId: string; label: string; disabled: boolean } | null>(null);

  const disabledServers: string[] = disabledServersData?.data?.disabledServers || [];

  // 5G Packages Hide/Show
  const [show5gModal, setShow5gModal] = useState(false);
  const { data: proxy5gData, isLoading: proxy5gLoading } = useGetProxy5gPackagesVisibilityQuery(undefined, { skip: !show5gModal });
  const [updateProxy5gPkg, { isLoading: isUpdatingPkg }] = useUpdateProxy5gPackageVisibilityMutation();
  const proxy5gPackages = proxy5gData?.data?.packages || [];
  const hiddenPkgs = proxy5gData?.data?.hiddenPackages || [];
  const proxy5gPkgDisplayNames: Record<number, string> = { 1: 'Premium Package', 2: 'Basic Package', 3: 'Premium Package Injection', 4: 'Ultimate Package', 5: 'Ultimate Package Injection' };
  const getPkgDisplayName = (pkgId: string | number, originalName: string) => proxy5gPkgDisplayNames[Number(pkgId)] || originalName;

  const { data: serverLabelsData } = useGetServerLabelsQuery();
  const serverLabelRows = flattenServerLabelGroups(serverLabelsData?.data);

  const SERVER_GROUPS = DASHBOARD_SERVER_GROUPS
    .filter((group) => (group.label === 'بروكسيات' ? hasProxies : hasNumbers))
    .map((group) => ({
    label: group.label,
    servers: group.servers.map((s) => ({
      id: s.id,
      name: resolveServerLabel(s.id, serverLabelRows, 'ar'),
    })),
  }));

  const getHiddenState = (pkgId: number) => {
    const entry = hiddenPkgs.find((h) => h.pkgId === pkgId);
    return { hiddenBot: entry?.hiddenBot || false, hiddenWebsite: entry?.hiddenWebsite || false };
  };

  const handleToggleBot = async (pkgId: number, current: boolean) => {
    try {
      await updateProxy5gPkg({ pkgId, hiddenBot: !current }).unwrap();
      toast.success(`تم ${!current ? 'إخفاء' : 'إظهار'} الباقة في البوت`);
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(error?.data?.message || error?.message || 'فشل تحديث الرؤية');
    }
  };

  const handleToggleWebsite = async (pkgId: number, current: boolean) => {
    try {
      await updateProxy5gPkg({ pkgId, hiddenWebsite: !current }).unwrap();
      toast.success(`تم ${!current ? 'إخفاء' : 'إظهار'} الباقة في الموقع`);
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(error?.data?.message || error?.message || 'فشل تحديث الرؤية');
    }
  };

  const handleToggleServer = async () => {
    if (!serverConfirm) return;
    try {
      await toggleServerDisabled({
        serverId: serverConfirm.serverId,
        data: { disabled: serverConfirm.disabled },
      }).unwrap();
      toast.success(serverConfirm.disabled ? `تم إيقاف السيرفر ${serverConfirm.label}` : `تم تشغيل السيرفر ${serverConfirm.label}`);
      setServerConfirm(null);
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(error?.data?.message || error?.message || 'فشل تحديث حالة السيرفر');
      setServerConfirm(null);
    }
  };

  const handleMaintenanceToggle = async (mode: 'none' | 'comprehensive' | 'bot_only' | 'website_only') => {
    if (mode === currentMode) return;
    setConfirmMode(mode);
  };

  const handleConfirmMaintenance = async () => {
    if (!confirmMode) return;
    try {
      const res = await updateMaintenanceMode({ mode: confirmMode as any }).unwrap();
      toast.success(res.message || `تم تحديث وضع الصيانة`);
      setConfirmMode(null);
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(error?.data?.message || error?.message || 'فشل تحديث وضع الصيانة');
      setConfirmMode(null);
    }
  };

  const getConfirmMessage = () => {
    const labels: Record<string, string> = {
      none: 'إلغاء الصيانة وتشغيل البوت للزبائن؟',
      bot_only:
        'تفعيل صيانة البوت؟ الزبائن لن يتمكنوا من استخدام البوت — حسابات الأدمن تبقى شغّالة.',
      website_only: 'تفعيل صيانة الموقع فقط؟ الزوار سيشاهدون صفحة الصيانة.',
      comprehensive: 'تفعيل الصيانة الشاملة؟ البوت والموقع سيتوقفان تماماً.',
    };
    return labels[confirmMode || ''] || '';
  };

  const isLoading = userStatsLoading || (!isTenantMode && (categoriesLoading || allProductsLoading));

  const userStats = userStatsData?.data.statistics;
  const categories = categoriesData?.data.categories || [];
  const allProductsPagination = allProductsData?.data.pagination;
  const availableProductsPagination = availableProductsData?.data.pagination;
  const soldProductsPagination = soldProductsData?.data.pagination;

  // Calculate statistics
  const totalCategories = categories.length;
  const hiddenCategories = categories.filter((cat) => cat.isHidden).length;
  const parentCategories = categories.filter((cat) => cat.isParent).length;

  const totalProducts = allProductsPagination?.total || 0;
  const availableProducts = availableProductsPagination?.total || 0;
  const soldProducts = soldProductsPagination?.total || 0;

  const formatMoney = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '—';
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return '—';
    return `$${n.toFixed(2)}`;
  };

  const masterPlanLabel = (() => {
    const plan = masterBalanceData?.data?.plan;
    if (!plan) return '—';
    const map: Record<string, string> = {
      numbers: 'أرقام',
      proxies: 'بروكسيات',
      both: 'أرقام + بروكسيات',
      full: 'كاملة',
    };
    return map[String(plan)] || String(plan);
  })();

  if (isTenantMode) {
    return (
      <>
        <TenantMerchantDashboard
          planLabel={masterPlanLabel}
          balanceLabel={formatMoney(
            masterBalanceData?.data?.balance ?? masterBalanceData?.data?.balances?.unified
          )}
          balanceValue={(() => {
            const v = masterBalanceData?.data?.balance ?? masterBalanceData?.data?.balances?.unified;
            const n = typeof v === 'number' ? v : Number(v);
            return Number.isFinite(n) ? n : null;
          })()}
          expiryLabel={(() => {
            const raw = masterBalanceData?.data?.subscription?.expiresAt;
            if (!raw) return null;
            const ms = new Date(raw).getTime() - Date.now();
            if (!Number.isFinite(ms)) return null;
            const days = Math.ceil(ms / (24 * 60 * 60 * 1000));
            if (days < 0) return `منتهي منذ ${Math.abs(days)} يوم`;
            if (days === 0) return 'ينتهي اليوم';
            return `${days} يوم متبقي`;
          })()}
          balanceLoading={masterBalanceLoading}
          userStats={userStats}
          statsLoading={userStatsLoading}
          hasNumbers={hasNumbers}
          hasProxies={hasProxies}
          currentMode={currentMode}
          maintenanceLoading={maintenanceLoading}
          isUpdatingMaintenance={isUpdatingMaintenance}
          onMaintenance={handleMaintenanceToggle}
          onOpenServers={() => setShowServerModal(true)}
        />

        <ConfirmDialog
          isOpen={confirmMode !== null}
          onClose={() => setConfirmMode(null)}
          onConfirm={handleConfirmMaintenance}
          title="تأكيد وضع الصيانة"
          message={getConfirmMessage()}
          confirmText="تأكيد"
          cancelText="إلغاء"
          type={confirmMode === 'comprehensive' ? 'danger' : confirmMode === 'none' ? 'info' : 'warning'}
          isLoading={isUpdatingMaintenance}
        />

        {showServerModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowServerModal(false);
            }}
          >
            <div
              className="card-glass tenant-scroll-modal rounded-2xl shadow-2xl p-6 md:p-8 max-w-2xl w-full max-h-[90vh] !overflow-y-auto overscroll-contain animate-scale-in"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-heading font-bold text-brand-accent flex items-center gap-2">
                  إيقاف سيرفر محدد
                </h2>
                <button
                  onClick={() => setShowServerModal(false)}
                  className="w-10 h-10 rounded-full bg-neutral-dark/50 hover:bg-neutral-dark flex items-center justify-center text-neutral-medium hover:text-neutral-light transition-colors"
                  aria-label="إغلاق"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {SERVER_GROUPS.map((group) => (
                <div key={group.label} className="mb-6 last:mb-0">
                  <h3 className="text-sm font-heading font-semibold text-neutral-medium mb-3">{group.label}</h3>
                  <div className="space-y-2">
                    {group.servers.map((server) => {
                      const isDisabled = disabledServers.includes(server.id);
                      return (
                        <div
                          key={server.id}
                          className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.08] bg-[#0a111c] px-4 py-3"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-heading font-semibold text-neutral-light truncate">{server.name}</p>
                            <p className={`text-xs font-body mt-0.5 ${isDisabled ? 'text-rose-400' : 'text-emerald-400'}`}>
                              {isDisabled ? 'متوقف' : 'يعمل'}
                            </p>
                          </div>
                          <button
                            type="button"
                            disabled={isTogglingServer}
                            onClick={() =>
                              setServerConfirm({
                                serverId: server.id,
                                label: server.name,
                                disabled: !isDisabled,
                              })
                            }
                            className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-heading font-semibold transition-colors ${
                              isDisabled
                                ? 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25'
                                : 'bg-rose-500/15 text-rose-400 hover:bg-rose-500/25'
                            }`}
                          >
                            {isDisabled ? 'تشغيل' : 'إيقاف'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <ConfirmDialog
          isOpen={serverConfirm !== null}
          onClose={() => setServerConfirm(null)}
          onConfirm={handleToggleServer}
          title={serverConfirm?.disabled ? 'تأكيد إيقاف السيرفر' : 'تأكيد تشغيل السيرفر'}
          message={
            serverConfirm
              ? serverConfirm.disabled
                ? `إيقاف السيرفر «${serverConfirm.label}»؟`
                : `تشغيل السيرفر «${serverConfirm.label}»؟`
              : ''
          }
          confirmText="تأكيد"
          cancelText="إلغاء"
          type={serverConfirm?.disabled ? 'danger' : 'info'}
          isLoading={isTogglingServer}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen animate-slide-up">
      {/* Header */}
      <div className="mb-8 text-center animate-slide-up">
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
          {t('admin.dashboard.title')}
        </h1>
        <p className="text-neutral-medium text-lg font-body">
          {t('admin.dashboard.subtitle')}
        </p>
      </div>

      {false && isTenantMode && (
        <div className="card-glass rounded-2xl p-6 mb-8">
          <h2 className="text-xl font-heading font-bold text-brand-accent mb-4">
            {t('admin.dashboard.masterBalance.title', 'رصيد الماستر')}
          </h2>
          {masterBalanceLoading ? (
            <ButtonSpinner />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl bg-brand-accent/10 border border-brand-accent/20 p-4">
                <p className="text-sm text-neutral-medium">{t('admin.dashboard.masterBalance.plan', 'الخطة')}</p>
                <p className="text-lg font-bold text-brand-accent">{masterPlanLabel}</p>
              </div>
              <div className="rounded-xl bg-brand-accent/10 border border-brand-accent/20 p-4">
                <p className="text-sm text-neutral-medium">
                  {t('admin.dashboard.masterBalance.unified', 'رصيد المتجر (موحّد)')}
                </p>
                <p className="text-2xl font-bold text-brand-accent tracking-tight">
                  {formatMoney(
                    masterBalanceData?.data?.balance ??
                      masterBalanceData?.data?.balances?.unified
                  )}
                </p>
                <p className="text-xs text-neutral-medium mt-1">
                  يُستخدم لشراء الأرقام والبروكسيات حسب خطتك
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Statistics Cards */}
      {!isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Users Statistics */}
          <div className="card-glass rounded-2xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-semibold text-neutral-light">
                {t('admin.dashboard.stats.users.title')}
              </h3>
              <div className="w-12 h-12 bg-brand-accent/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-brand-accent"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.dashboard.stats.users.total')}:
                </span>
                <span className="text-lg font-heading font-bold text-brand-accent">
                  {userStats?.totalUsers || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.dashboard.stats.users.active')}:
                </span>
                <span className="text-sm font-heading font-semibold text-green-500">
                  {userStats?.activeUsers || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.dashboard.stats.users.banned')}:
                </span>
                <span className="text-sm font-heading font-semibold text-red-500">
                  {userStats?.bannedUsers || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.dashboard.stats.users.admins')}:
                </span>
                <span className="text-sm font-heading font-semibold text-brand-accent">
                  {userStats?.adminUsers || 0}
                </span>
              </div>
            </div>
          </div>

          {!isTenantMode && (
          <>
          {/* Categories Statistics */}
          <div className="card-glass rounded-2xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-semibold text-neutral-light">
                {t('admin.dashboard.stats.categories.title')}
              </h3>
              <div className="w-12 h-12 bg-brand-accent/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-brand-accent"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.dashboard.stats.categories.total')}:
                </span>
                <span className="text-lg font-heading font-bold text-brand-accent">
                  {totalCategories}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.dashboard.stats.categories.parents')}:
                </span>
                <span className="text-sm font-heading font-semibold text-neutral-light">
                  {parentCategories}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.dashboard.stats.categories.hidden')}:
                </span>
                <span className="text-sm font-heading font-semibold text-yellow-500">
                  {hiddenCategories}
                </span>
              </div>
            </div>
          </div>

          {/* Products Statistics */}
          <div className="card-glass rounded-2xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-semibold text-neutral-light">
                {t('admin.dashboard.stats.products.title')}
              </h3>
              <div className="w-12 h-12 bg-brand-accent/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-brand-accent"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.dashboard.stats.products.total')}:
                </span>
                <span className="text-lg font-heading font-bold text-brand-accent">
                  {totalProducts}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.dashboard.stats.products.available')}:
                </span>
                <span className="text-sm font-heading font-semibold text-green-500">
                  {availableProducts}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.dashboard.stats.products.sold')}:
                </span>
                <span className="text-sm font-heading font-semibold text-red-500">
                  {soldProducts}
                </span>
              </div>
            </div>
          </div>
          </>
          )}

          {/* Quick Actions */}
          <div className="card-glass rounded-2xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-semibold text-neutral-light">
                {t('admin.dashboard.stats.quickActions.title')}
              </h3>
              <div className="w-12 h-12 bg-brand-accent/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-brand-accent"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
            </div>
            <div className="space-y-2">
              {isTenantMode ? (
              <>
              <Link
                to="/admin/users"
                className="block w-full px-4 py-2 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body text-center"
              >
                {t('admin.dashboard.users.title')}
              </Link>
              <Link
                to="/admin/transactions"
                className="block w-full px-4 py-2 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body text-center"
              >
                {t('admin.dashboard.transactions.title')}
              </Link>
              <Link
                to="/admin/payment-gateways"
                className="block w-full px-4 py-2 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body text-center"
              >
                {t('admin.dashboard.paymentGateways.title')}
              </Link>
              <Link
                to="/admin/user-margins"
                className="block w-full px-4 py-2 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body text-center"
              >
                {t('admin.nav.userMargins')}
              </Link>
              </>
              ) : (
              <>
              <Link
                to="/admin/categories/new"
                className="block w-full px-4 py-2 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body text-center"
              >
                {t('admin.dashboard.stats.quickActions.newCategory')}
              </Link>
              <Link
                to="/admin/products/new"
                className="block w-full px-4 py-2 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body text-center"
              >
                {t('admin.dashboard.stats.quickActions.newProduct')}
              </Link>
              </>
              )}
            </div>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="mb-8 text-center py-16">
          <p className="text-neutral-medium text-xl font-body animate-pulse">
            {t('common.loading')}
          </p>
        </div>
      )}

      {/* Admin Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-scale-in">
        {!isTenantMode && (
        <>
        {/* Categories Management Card */}
        <Link
          to="/admin/categories"
          className="block card-glass rounded-lg p-6 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group"
        >
          <div className="flex flex-col items-center text-center">
            {/* Icon */}
            <div className="w-16 h-16 bg-brand-accent/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-brand-accent/30 transition-colors">
              <svg
                className="w-8 h-8 text-brand-accent"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>

            {/* Title */}
            <h3 className="text-xl font-heading font-bold text-brand-accent mb-2 group-hover:text-brand-accent/80 transition-colors">
              {t('admin.dashboard.categories.title')}
            </h3>

            {/* Description */}
            <p className="text-sm text-neutral-medium font-body">
              {t('admin.dashboard.categories.description')}
            </p>
          </div>
        </Link>

        {/* Placeholder for future admin features */}
        {/* Products Management Card */}
        <Link
          to="/admin/products"
          className="block card-glass rounded-lg p-6 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group"
        >
          <div className="flex flex-col items-center text-center">
            {/* Icon */}
            <div className="w-16 h-16 bg-brand-accent/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-brand-accent/30 transition-colors">
              <svg
                className="w-8 h-8 text-brand-accent"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
            </div>

            {/* Title */}
            <h3 className="text-xl font-heading font-bold text-brand-accent mb-2 group-hover:text-brand-accent/80 transition-colors">
              {t('admin.dashboard.products.title')}
            </h3>

            {/* Description */}
            <p className="text-sm text-neutral-medium font-body">
              {t('admin.dashboard.products.description')}
            </p>
          </div>
        </Link>
        </>
        )}

        {/* Tenant merchant settings — email, branding, store */}
        {isTenantMode && (
          <>
            <Link
              to="/admin/email-settings"
              className="block card-glass rounded-lg p-6 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-brand-accent/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-brand-accent/30 transition-colors">
                  <svg className="w-8 h-8 text-brand-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-bold text-brand-accent mb-2">إعدادات الإيميل</h3>
                <p className="text-sm text-neutral-medium font-body">Gmail / SMTP — أكواد التحقق لزبائنك</p>
              </div>
            </Link>
            <Link
              to="/admin/branding"
              className="block card-glass rounded-lg p-6 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-brand-accent/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-brand-accent/30 transition-colors">
                  <svg className="w-8 h-8 text-brand-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-bold text-brand-accent mb-2">العلامة التجارية</h3>
                <p className="text-sm text-neutral-medium font-body">الاسم والشعار وروابط متجرك</p>
              </div>
            </Link>
            <Link
              to="/admin/store-settings"
              className="block card-glass rounded-lg p-6 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-brand-accent/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-brand-accent/30 transition-colors">
                  <svg className="w-8 h-8 text-brand-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-bold text-brand-accent mb-2">إعدادات المتجر</h3>
                <p className="text-sm text-neutral-medium font-body">أسماء السيرفرات وطرق الدفع</p>
              </div>
            </Link>
          </>
        )}

        {/* Users, payments, margins, maintenance tools — available to tenant merchants */}
        {/* Users Management Card */}
        <Link
          to="/admin/users"
          className="block card-glass rounded-lg p-6 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group"
        >
          <div className="flex flex-col items-center text-center">
            {/* Icon */}
            <div className="w-16 h-16 bg-brand-accent/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-brand-accent/30 transition-colors">
              <svg
                className="w-8 h-8 text-brand-accent"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            </div>

            {/* Title */}
            <h3 className="text-xl font-heading font-bold text-brand-accent mb-2 group-hover:text-brand-accent/80 transition-colors">
              {t('admin.dashboard.users.title')}
            </h3>

            {/* Description */}
            <p className="text-sm text-neutral-medium font-body">
              {t('admin.dashboard.users.description')}
            </p>
          </div>
        </Link>

        {/* System Configuration Card */}
        <Link
          to="/admin/system-config"
          className="block card-glass rounded-lg p-6 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group"
        >
          <div className="flex flex-col items-center text-center">
            {/* Icon */}
            <div className="w-16 h-16 bg-brand-accent/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-brand-accent/30 transition-colors">
              <svg
                className="w-8 h-8 text-brand-accent"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>

            {/* Title */}
            <h3 className="text-xl font-heading font-bold text-brand-accent mb-2 group-hover:text-brand-accent/80 transition-colors">
              {t('admin.dashboard.systemConfig.title')}
            </h3>

            {/* Description */}
            <p className="text-sm text-neutral-medium font-body">
              {t('admin.dashboard.systemConfig.description')}
            </p>
          </div>
        </Link>

        {/* External API Keys Card — hidden in tenant SaaS mode */}
        {!isTenantMode && (
        <Link
          to="/admin/external-api-keys"
          className="block card-glass rounded-lg p-6 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group"
        >
          <div className="flex flex-col items-center text-center">
            {/* Icon */}
            <div className="w-16 h-16 bg-brand-accent/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-brand-accent/30 transition-colors">
              <svg
                className="w-8 h-8 text-brand-accent"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
            </div>

            {/* Title */}
            <h3 className="text-xl font-heading font-bold text-brand-accent mb-2 group-hover:text-brand-accent/80 transition-colors">
              {t('admin.dashboard.externalApiKeys.title')}
            </h3>

            {/* Description */}
            <p className="text-sm text-neutral-medium font-body">
              {t('admin.dashboard.externalApiKeys.description')}
            </p>
          </div>
        </Link>
        )}

        {/* Payment Gateways Card */}
        <Link
          to="/admin/payment-gateways"
          className="block card-glass rounded-lg p-6 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group"
        >
          <div className="flex flex-col items-center text-center">
            {/* Icon */}
            <div className="w-16 h-16 bg-brand-accent/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-brand-accent/30 transition-colors">
              <svg
                className="w-8 h-8 text-brand-accent"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
            </div>

            {/* Title */}
            <h3 className="text-xl font-heading font-bold text-brand-accent mb-2 group-hover:text-brand-accent/80 transition-colors">
              {t('admin.dashboard.paymentGateways.title')}
            </h3>

            {/* Description */}
            <p className="text-sm text-neutral-medium font-body">
              {t('admin.dashboard.paymentGateways.description')}
            </p>
          </div>
        </Link>

        {/* Transactions Management Card */}
        <Link
          to="/admin/transactions"
          className="block card-glass rounded-lg p-6 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group"
        >
          <div className="flex flex-col items-center text-center">
            {/* Icon */}
            <div className="w-16 h-16 bg-brand-accent/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-brand-accent/30 transition-colors">
              <svg
                className="w-8 h-8 text-brand-accent"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
            </div>

            {/* Title */}
            <h3 className="text-xl font-heading font-bold text-brand-accent mb-2 group-hover:text-brand-accent/80 transition-colors">
              {t('admin.dashboard.transactions.title')}
            </h3>

            {/* Description */}
            <p className="text-sm text-neutral-medium font-body">
              {t('admin.dashboard.transactions.description')}
            </p>
          </div>
        </Link>

        {/* Numbers Management Card */}
        {hasNumbers && (
        <Link
          to="/admin/numbers"
          className="block card-glass rounded-lg p-6 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-brand-accent/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-brand-accent/30 transition-colors">
              <svg className="w-8 h-8 text-brand-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h14a2 2 0 012 2v3H3V5zm0 5h18v9a2 2 0 01-2 2H5a2 2 0 01-2-2v-9zm4 3h4m-4 3h7" />
              </svg>
            </div>
            <h3 className="text-xl font-heading font-bold text-brand-accent mb-2 group-hover:text-brand-accent/80 transition-colors">
              {t('admin.dashboard.numbers.title')}
            </h3>
            <p className="text-sm text-neutral-medium font-body">
              {t('admin.dashboard.numbers.description')}
            </p>
          </div>
        </Link>
        )}

        {/* Proxies Management Card */}
        {hasProxies && (
        <Link
          to="/admin/proxies"
          className="block card-glass rounded-lg p-6 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-brand-accent/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-brand-accent/30 transition-colors">
              <svg className="w-8 h-8 text-brand-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0-1.657 1.79-3 4-3s4 1.343 4 3-1.79 3-4 3m-8-6c0-1.657 1.79-3 4-3s4 1.343 4 3-1.79 3-4 3-4-1.343-4-3zm0 6c0-1.657 1.79-3 4-3s4 1.343 4 3-1.79 3-4 3-4-1.343-4-3zm-4 0c0-1.657 1.79-3 4-3" />
              </svg>
            </div>
            <h3 className="text-xl font-heading font-bold text-brand-accent mb-2 group-hover:text-brand-accent/80 transition-colors">
              {t('admin.dashboard.proxies.title')}
            </h3>
            <p className="text-sm text-neutral-medium font-body">
              {t('admin.dashboard.proxies.description')}
            </p>
          </div>
        </Link>
        )}

        {/* User Margins Card */}
        <Link
          to="/admin/user-margins"
          className="block card-glass rounded-lg p-6 hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/20 transform hover:scale-105 transition-all duration-300 group"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-brand-accent/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-brand-accent/30 transition-colors">
              <svg className="w-8 h-8 text-brand-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-heading font-bold text-brand-accent mb-2 group-hover:text-brand-accent/80 transition-colors">
              هوامش المستخدمين
            </h3>
            <p className="text-sm text-neutral-medium font-body">
              تعيين هامش ربح خاص لكل مستخدم على كل سيرفر
            </p>
          </div>
        </Link>
      </div>

      {/* Maintenance Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmMode !== null}
        onClose={() => setConfirmMode(null)}
        onConfirm={handleConfirmMaintenance}
        title="تأكيد وضع الصيانة"
        message={getConfirmMessage()}
        confirmText="تأكيد"
        cancelText="إلغاء"
        type={confirmMode === 'comprehensive' ? 'danger' : confirmMode === 'none' ? 'info' : 'warning'}
        isLoading={isUpdatingMaintenance}
      />

      {/* Server Stop Confirmation Dialog - rendered inside modal below */}

      {/* Maintenance Mode Section */}
      <div className="mt-8 animate-scale-in">
        <div className="card-glass rounded-2xl shadow-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-heading font-bold text-brand-accent">
              وضع الصيانة
            </h2>
            {maintenanceLoading && <ButtonSpinner size="xs" />}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* None */}
            <button
              onClick={() => handleMaintenanceToggle('none')}
              disabled={isUpdatingMaintenance}
              className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                currentMode === 'none'
                  ? 'border-green-500 bg-green-500/10 shadow-lg shadow-green-500/20'
                  : 'border-neutral-dark bg-neutral-dark/30 hover:border-green-500/50'
              }`}
            >
              <div className="text-center">
                <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${
                  currentMode === 'none' ? 'bg-green-500/20' : 'bg-neutral-dark'
                }`}>
                  <svg className={`w-6 h-6 ${currentMode === 'none' ? 'text-green-500' : 'text-neutral-medium'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className={`font-heading font-bold text-sm ${currentMode === 'none' ? 'text-green-500' : 'text-neutral-light'}`}>
                  لا شيء
                </h3>
                <p className="text-xs text-neutral-medium font-body mt-1">الكل شغال</p>
              </div>
            </button>

            {/* Bot Only */}
            <button
              onClick={() => handleMaintenanceToggle('bot_only')}
              disabled={isUpdatingMaintenance}
              className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                currentMode === 'bot_only'
                  ? 'border-yellow-500 bg-yellow-500/10 shadow-lg shadow-yellow-500/20'
                  : 'border-neutral-dark bg-neutral-dark/30 hover:border-yellow-500/50'
              }`}
            >
              <div className="text-center">
                <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${
                  currentMode === 'bot_only' ? 'bg-yellow-500/20' : 'bg-neutral-dark'
                }`}>
                  <svg className={`w-6 h-6 ${currentMode === 'bot_only' ? 'text-yellow-500' : 'text-neutral-medium'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className={`font-heading font-bold text-sm ${currentMode === 'bot_only' ? 'text-yellow-500' : 'text-neutral-light'}`}>
                  صيانة البوت
                </h3>
                <p className="text-xs text-neutral-medium font-body mt-1">البوت فقط</p>
              </div>
            </button>

            {/* Website Only */}
            <button
              onClick={() => handleMaintenanceToggle('website_only')}
              disabled={isUpdatingMaintenance}
              className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                currentMode === 'website_only'
                  ? 'border-orange-500 bg-orange-500/10 shadow-lg shadow-orange-500/20'
                  : 'border-neutral-dark bg-neutral-dark/30 hover:border-orange-500/50'
              }`}
            >
              <div className="text-center">
                <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${
                  currentMode === 'website_only' ? 'bg-orange-500/20' : 'bg-neutral-dark'
                }`}>
                  <svg className={`w-6 h-6 ${currentMode === 'website_only' ? 'text-orange-500' : 'text-neutral-medium'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                </div>
                <h3 className={`font-heading font-bold text-sm ${currentMode === 'website_only' ? 'text-orange-500' : 'text-neutral-light'}`}>
                  صيانة الموقع
                </h3>
                <p className="text-xs text-neutral-medium font-body mt-1">الموقع فقط</p>
              </div>
            </button>

            {/* Comprehensive */}
            <button
              onClick={() => handleMaintenanceToggle('comprehensive')}
              disabled={isUpdatingMaintenance}
              className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                currentMode === 'comprehensive'
                  ? 'border-red-500 bg-red-500/10 shadow-lg shadow-red-500/20'
                  : 'border-neutral-dark bg-neutral-dark/30 hover:border-red-500/50'
              }`}
            >
              <div className="text-center">
                <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${
                  currentMode === 'comprehensive' ? 'bg-red-500/20' : 'bg-neutral-dark'
                }`}>
                  <svg className={`w-6 h-6 ${currentMode === 'comprehensive' ? 'text-red-500' : 'text-neutral-medium'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className={`font-heading font-bold text-sm ${currentMode === 'comprehensive' ? 'text-red-500' : 'text-neutral-light'}`}>
                  صيانة شاملة
                </h3>
                <p className="text-xs text-neutral-medium font-body mt-1">البوت والموقع</p>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* إيقاف سيرفر محدد Button */}
      <div className="mt-6 animate-scale-in">
        <button
          onClick={() => setShowServerModal(true)}
          className="w-full card-glass rounded-2xl shadow-2xl p-6 group hover:border-red-500/50 hover:shadow-lg hover:shadow-red-500/10 transition-all duration-300"
        >
          <div className="flex items-center justify-center gap-3">
            <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center group-hover:bg-red-500/30 transition-colors">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            <div className="text-right">
              <h3 className="text-xl font-heading font-bold text-neutral-light group-hover:text-red-400 transition-colors">
                إيقاف سيرفر محدد
              </h3>
              <p className="text-sm text-neutral-medium font-body">
                إيقاف أو تشغيل سيرفر معين بشكل منفصل
              </p>
            </div>
            <svg className="w-5 h-5 text-neutral-medium mr-auto group-hover:text-red-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </button>
      </div>

      {/* 5G Packages Hide/Show — master platform only (needs provider API keys) */}
      {!isTenantMode && (
      <>
      <div className="mt-6 animate-scale-in">
        <button
          onClick={() => setShow5gModal(true)}
          className="w-full card-glass rounded-2xl shadow-2xl p-6 group hover:border-brand-accent/50 hover:shadow-lg hover:shadow-brand-accent/10 transition-all duration-300"
        >
          <div className="flex items-center justify-center gap-3">
            <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
              <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <div className="text-right">
              <h3 className="text-xl font-heading font-bold text-neutral-light group-hover:text-purple-400 transition-colors">
                إخفاء - إظهار
              </h3>
              <p className="text-sm text-neutral-medium font-body">
                إخفاء أو إظهار باقات 5G في البوت أو الموقع
              </p>
            </div>
            <svg className="w-5 h-5 text-neutral-medium mr-auto group-hover:text-purple-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </button>
      </div>

      {/* 5G Packages Hide/Show Modal */}
      {show5gModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={(e) => { if (e.target === e.currentTarget) setShow5gModal(false); }}
        >
          <div
            className="card-glass tenant-scroll-modal rounded-2xl shadow-2xl p-6 md:p-8 max-w-2xl w-full max-h-[90vh] !overflow-y-auto overscroll-contain animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-heading font-bold text-brand-accent flex items-center gap-2">
                <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                إخفاء - إظهار باقات 5G
              </h2>
              <button
                onClick={() => setShow5gModal(false)}
                className="w-8 h-8 rounded-full bg-neutral-dark/50 flex items-center justify-center text-neutral-medium hover:text-neutral-light hover:bg-neutral-dark transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-neutral-medium font-body text-sm mb-6">
              تحكم بإظهار أو إخفاء باقات 5G. الباقات المخفية لن تظهر للمستخدمين في البوت أو الموقع.
            </p>

            {proxy5gLoading && (
              <div className="text-center py-8">
                <p className="text-neutral-medium text-lg font-body animate-pulse">جاري تحميل الباقات...</p>
              </div>
            )}

            {!proxy5gLoading && proxy5gPackages.length === 0 && (
              <div className="text-center py-8">
                <p className="text-neutral-medium text-lg font-body">لا توجد باقات متاحة. تأكد من إعدادات API لـ 5G.</p>
              </div>
            )}

            {!proxy5gLoading && proxy5gPackages.length > 0 && (
              <div className="space-y-4">
                {proxy5gPackages.map((pkg) => {
                  const state = getHiddenState(pkg.id);
                  return (
                    <div
                      key={pkg.id}
                      className="rounded-xl border border-white/10 bg-neutral-dark/30 p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-heading font-bold text-neutral-light text-lg">{getPkgDisplayName(pkg.id, pkg.name)}</h3>
                        <span className="text-xs text-neutral-medium font-body">ID: {pkg.id}</span>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleToggleBot(pkg.id, state.hiddenBot)}
                          disabled={isUpdatingPkg}
                          className={`flex-1 py-2 px-4 rounded-lg font-heading font-semibold text-sm transition-all flex items-center justify-center gap-2 border ${
                            state.hiddenBot
                              ? 'bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20'
                              : 'bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20'
                          } disabled:opacity-50`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {state.hiddenBot ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            )}
                          </svg>
                          {state.hiddenBot ? 'مخفي في البوت' : 'ظاهر في البوت'}
                        </button>
                        <button
                          onClick={() => handleToggleWebsite(pkg.id, state.hiddenWebsite)}
                          disabled={isUpdatingPkg}
                          className={`flex-1 py-2 px-4 rounded-lg font-heading font-semibold text-sm transition-all flex items-center justify-center gap-2 border ${
                            state.hiddenWebsite
                              ? 'bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20'
                              : 'bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20'
                          } disabled:opacity-50`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {state.hiddenWebsite ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            )}
                          </svg>
                          {state.hiddenWebsite ? 'مخفي في الموقع' : 'ظاهر في الموقع'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      </>
      )}

      {/* Server Control Modal */}
      {showServerModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={(e) => { if (e.target === e.currentTarget) setShowServerModal(false); }}
        >
          <div
            className="card-glass tenant-scroll-modal rounded-2xl shadow-2xl p-6 md:p-8 max-w-2xl w-full max-h-[90vh] !overflow-y-auto overscroll-contain animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-heading font-bold text-brand-accent flex items-center gap-2">
                <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                إيقاف سيرفر محدد
              </h2>
              <button
                onClick={() => setShowServerModal(false)}
                className="w-8 h-8 rounded-full bg-neutral-dark/50 flex items-center justify-center text-neutral-medium hover:text-neutral-light hover:bg-neutral-dark transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-neutral-medium font-body text-sm mb-6">
              اختر السيرفر الذي تريد إيقافه. السيرفرات المتوقفة سيتم تعطيلها للمستخدمين في البوت والموقع.
            </p>

            {/* Inline Confirmation inside modal */}
            {serverConfirm && (
              <div className="mb-6 p-5 rounded-xl border-2 border-red-500/40 bg-red-500/10 animate-scale-in">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-sm text-red-400">
                      {serverConfirm.disabled ? 'تأكيد إيقاف السيرفر' : 'تأكيد تشغيل السيرفر'}
                    </h3>
                    <p className="text-sm text-neutral-medium font-body mt-1">
                      {serverConfirm.disabled
                        ? `هل أنت متأكد من إيقاف "${serverConfirm.label}"؟ المستخدمين لن يتمكنوا من استخدامه في البوت والموقع.`
                        : `هل أنت متأكد من تشغيل "${serverConfirm.label}"؟`}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { void handleToggleServer(); }}
                    disabled={isTogglingServer}
                    className={`flex-1 py-2.5 px-4 rounded-lg font-heading font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                      serverConfirm.disabled
                        ? 'bg-red-500 text-white hover:bg-red-600'
                        : 'bg-green-500 text-white hover:bg-green-600'
                    } disabled:opacity-50`}
                  >
                    {isTogglingServer ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : null}
                    {serverConfirm.disabled ? 'إيقاف' : 'تشغيل'}
                  </button>
                  <button
                    onClick={() => setServerConfirm(null)}
                    disabled={isTogglingServer}
                    className="flex-1 py-2.5 px-4 bg-neutral-dark text-neutral-light rounded-lg font-heading font-semibold text-sm hover:bg-neutral-dark/80 transition-all disabled:opacity-50"
                  >
                    إلغاء
                  </button>
                </div>
              </div>
            )}

            {/* Server Groups */}
            <div className="space-y-6">
              {SERVER_GROUPS.map((group) => (
                <div key={group.label}>
                  <h3 className="text-sm font-heading font-semibold text-brand-accent mb-3 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-accent/60" />
                    {group.label}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {group.servers.map((server) => {
                      const isDisabled = disabledServers.includes(server.id);
                      return (
                        <div
                          key={`${group.label}-${server.id}`}
                          className={`rounded-xl border transition-all duration-200 ${
                            isDisabled
                              ? 'border-red-500/50 bg-red-500/5'
                              : 'border-white/10 bg-neutral-dark/30 hover:border-green-500/30'
                          }`}
                        >
                          <div className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2.5">
                                <span className={`w-2.5 h-2.5 rounded-full ${
                                  isDisabled
                                    ? 'bg-red-500 shadow-sm shadow-red-500/40 animate-pulse'
                                    : 'bg-green-500 shadow-sm shadow-green-500/40'
                                }`} />
                                <div>
                                  <h4 className="font-heading font-semibold text-sm text-neutral-light">
                                    {server.name}
                                  </h4>
                                </div>
                              </div>
                              <span className={`text-[11px] font-heading font-semibold px-2 py-0.5 rounded-full ${
                                isDisabled
                                  ? 'bg-red-500/20 text-red-400'
                                  : 'bg-green-500/20 text-green-400'
                              }`}>
                                {isDisabled ? 'متوقف' : 'شغال'}
                              </span>
                            </div>

                            <div className="mt-3">
                              <button
                                onClick={() => setServerConfirm({ serverId: server.id, label: server.name, disabled: !isDisabled })}
                                disabled={isTogglingServer}
                                className={`w-full py-2 px-3 text-sm font-heading font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 ${
                                  isDisabled
                                    ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30'
                                    : 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30'
                                } disabled:opacity-50`}
                              >
                                {isDisabled ? (
                                  <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    تشغيل
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    إيقاف
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

